import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  MODAL_BACKDROP_INNER_CLASS,
  MODAL_MAX_H,
  MODAL_PANEL_CLASS,
  modalWidthStyle,
  type ModalWidthVar,
} from '@/styles/modalTokens';
import { useModalMotion } from '@/hooks/useModalMotion';
import { wfSectionTitleStyle } from '@/styles/typography';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** When false (default), backdrop blocks the page but does not dismiss. */
  closeOnOverlayClick?: boolean;
  className?: string;
}

const SIZE_VARS: Record<NonNullable<ModalProps['size']>, ModalWidthVar> = {
  sm: '--wf-mdl-w',
  md: '--wf-mdl-payout-w',
  lg: '--wf-mdl-withdraw-w',
  xl: '--wf-mdl-game-w',
  full: '--wf-mdl-wide-w',
};

/**
 * Shared Modal component — cosmos panel + --wf-mdl-* width tokens.
 * Portaled to document.body so page stacking contexts cannot let clicks through.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = false,
  className = '',
}) => {
  const { render, exiting, backdropClass, panelClass } = useModalMotion(isOpen);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && render && !exiting) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [render, exiting, onClose]);

  useEffect(() => {
    if (render) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [render]);

  if (!render) return null;

  const pad = 'var(--wf-mdl-pad)';

  const onBackdropClick = () => {
    if (closeOnOverlayClick && !exiting) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--wf-z-modal-nested)] flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        className={`${MODAL_BACKDROP_INNER_CLASS} ${backdropClass}`}
        aria-hidden
        onClick={onBackdropClick}
        onPointerDown={(e) => e.stopPropagation()}
      />
      <div
        className={`${MODAL_PANEL_CLASS} ${panelClass} ${className}`}
        style={{
          ...modalWidthStyle(SIZE_VARS[size]),
          maxHeight: MODAL_MAX_H,
          borderRadius: 'var(--wf-mdl-radius)',
        }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {title && (
          <div
            className="flex items-center justify-between shrink-0 border-b border-border-ice"
            style={{ padding: pad }}
          >
            <h2 className="font-display font-bold text-ink" style={wfSectionTitleStyle}>
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-caption-muted hover:text-ink transition-colors shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 cosmos-scroll-y" style={{ padding: pad }}>
          {children}
        </div>

        {footer && (
          <div
            className="shrink-0 flex items-center justify-end gap-3 border-t border-border-ice"
            style={{ padding: pad }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
